import React, { useState, useEffect } from "react";
import BrandLogo from "../common/BrandLogo";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

const FONT = "-apple-system,'Segoe UI',Helvetica,sans-serif";

// Use same color constants as NectarOnboard
const GOLD     = "#0077B5";
const GOLD_DIM = "#005885";
const GOLD_DARK= "#EBF5FB";
const BG       = "#F3F2EF";
const CARD     = "#FFFFFF";
const BORDER   = "#E1E9EE";

const COLORS = {
  blue:     "#0077B5",
  blueDk:   "#005885",
  green:    "#1D9E75",
  bg:       "#F3F2EF",
  card:     "#FFFFFF",
  border:   "#E1E9EE",
  text:     "#1A1A1A",
  muted:    "#666666",
  active:   "#0077B5",
  complete: "#1D9E75",
  upcoming: "#ccc",
};

// ─── MBTI (same as NectarOnboard) ─────────────────────────────────────────────
const MBTI_TYPES = [
  { type: "INTJ", label: "Architect"    }, { type: "INTP", label: "Logician"     },
  { type: "ENTJ", label: "Commander"    }, { type: "ENTP", label: "Debater"      },
  { type: "INFJ", label: "Advocate"     }, { type: "INFP", label: "Mediator"     },
  { type: "ENFJ", label: "Protagonist"  }, { type: "ENFP", label: "Campaigner"   },
  { type: "ISTJ", label: "Logistician"  }, { type: "ISTP", label: "Virtuoso"     },
  { type: "ESTJ", label: "Executive"    }, { type: "ESTP", label: "Entrepreneur" },
  { type: "ISFJ", label: "Defender"     }, { type: "ISFP", label: "Adventurer"   },
  { type: "ESFJ", label: "Consul"       }, { type: "ESFP", label: "Entertainer"  },
];

const MBTI_DESCS = {
  INTJ: "Strategic and precise — cuts straight to the structural insight or long-term implication",
  INTP: "Intellectually curious — highlights overlooked angles, paradoxes, and underlying principles",
  ENTJ: "Commanding and decisive — leads with the strategic takeaway or business implication",
  ENTP: "Sharp and provocative — challenges conventional wisdom and flips the narrative",
  INFJ: "Purpose-driven and insightful — connects topics to deeper human truth or pattern",
  INFP: "Warm and idealistic — frames topics through a human or emotional lens",
  ENFJ: "Charismatic and people-first — ties insight to how it impacts people or teams",
  ENFP: "Energetic and imaginative — highlights exciting possibilities and human connections",
  ISTJ: "Measured and factual — references specific data, facts, or proven methods",
  ISFJ: "Genuine and helpful — frames topics around how they serve or protect others",
  ESTJ: "Confident and structured — leads with the actionable insight or operational lesson",
  ESFJ: "Friendly and inclusive — connects topics to teamwork, culture, or shared values",
  ISTP: "Concise and pragmatic — focuses on how things work or what's mechanically interesting",
  ISFP: "Understated and authentic — finds the human or aesthetic angle in every topic",
  ESTP: "Bold and high-energy — gets to the point fast, leads with what happened and why it matters",
  ESFP: "Upbeat and relatable — makes topics feel alive and relevant to everyday experience",
};

// ─── Tones (same as NectarOnboard) ────────────────────────────────────────────
const TONES = [
  { id: "professional",   label: "Professional",   fullDesc: "Polished and authoritative — a thought-leader voice that lands on a strategic insight." },
  { id: "conversational", label: "Conversational", fullDesc: "Warm and human, like texting a smart friend." },
  { id: "bold",           label: "Bold",           fullDesc: "Punchy and provocative. Short sentences, no hedging." },
  { id: "educational",    label: "Educational",    fullDesc: "Clear and value-first — teaches one thing and shows why it matters." },
  { id: "storytelling",   label: "Storytelling",   fullDesc: "Narrative and personal. Draws readers in with a human scene." },
];

// ─── Categories (same as NectarOnboard) ───────────────────────────────────────
const CATEGORIES = [
  {
    id: "world_news", label: "World News", icon: "🌎", sub: "World Headlines",
    subtopics: [{ label: "World Headlines", slugs: ["spanglishreporter.substack.com/p"] }],
  },
  {
    id: "hispanic_us", label: "US Regional News", icon: "🇺🇸", sub: "US News · San Jose · El Paso · Houston",
    subtopics: [
      { label: "US News",    slugs: ["mercadonews.com/blogs/national", "spanglishusa.substack.com/p", "spanglish.ca/blogs/world-page/united-states"] },
      { label: "San Jose",   slugs: ["mercadonews.com/blogs/national/san-jose", "spanglish.ca/blogs/world-page/united-states/san-jose"] },
      { label: "El Paso",    slugs: ["mercadonews.com/blogs/national/el-paso", "spanglish.ca/blogs/world-page/united-states/el-paso"] },
      { label: "Houston",    slugs: ["spanglish.ca/blogs/world-page/united-states/houston"] },
      { label: "East Coast", slugs: ["spanglish.ca/blogs/world-page/united-states/east-coast"] },
    ],
  },
  {
    id: "canadian_regional", label: "Canadian Regional News", icon: "🍁", sub: "Canada · Toronto · BC · Montreal",
    subtopics: [
      { label: "Canada",   slugs: ["spanglishcanada.substack.com/p"] },
      { label: "Toronto",  slugs: ["spanglish.ca/blogs/world-page/canada-page/toronto"] },
      { label: "BC",       slugs: ["spanglish.ca/blogs/world-page/canada-page/bc"] },
      { label: "Montreal", slugs: ["spanglish.ca/blogs/world-page/canada-page/montreal"] },
      { label: "Calgary",  slugs: ["spanglish.ca/blogs/world-page/calgary"] },
    ],
  },
  {
    id: "canada_espanol", label: "Canada en Español", icon: "🇨🇦", sub: "Toronto · Calgary · BC · Montreal",
    subtopics: [
      { label: "Toronto",                    slugs: ["hispanosencanada.ca/blogs/canada/toronto"] },
      { label: "Calgary",                    slugs: ["hispanosencanada.ca/blogs/canada/calgary"] },
      { label: "British Columbia",           slugs: ["hispanosencanada.ca/blogs/canada/columbia-britanica"] },
      { label: "Montreal",                   slugs: ["hispanosencanada.ca/blogs/canada/montreal"] },
      { label: "New Brunswick & East Coast", slugs: ["hispanosencanada.ca/blogs/canada/costa-este"] },
      { label: "Vida Activa",                slugs: ["hispanosencanada.ca/blogs/rumbo"] },
      { label: "Negocio Propio",             slugs: ["hispanosencanada.ca/blogs/impulso"] },
    ],
  },
  {
    id: "real_estate", label: "Real Estate & Housing", icon: "🏠", sub: "Housing · Investment · Retirement",
    subtopics: [
      { label: "Real Estate",          slugs: ["micasita.ca/blogs/real-estate"] },
      { label: "Real Estate Insights", slugs: ["micasita.ca/blogs/real-estate-insights"] },
      { label: "Retirement Taxes",     slugs: ["micasita.ca/blogs/retirement-taxes"] },
      { label: "Retirement Homes",     slugs: ["micasita.ca/blogs/retirement-homes"] },
      { label: "Rental Property",      slugs: ["micasita.ca/blogs/rental-property"] },
      { label: "Retire Abroad",        slugs: ["micasita.ca/blogs/retire-abroad"] },
      { label: "Home Price Trends",    slugs: ["micasita.ca/blogs/home-price-trends"] },
      { label: "Housing News",         slugs: ["micasita.ca/blogs/housing-news"] },
      { label: "Los Angeles",          slugs: ["micasita.ca/blogs/los-angeles"] },
      { label: "San Jose",             slugs: ["micasita.ca/blogs/san-jose"] },
      { label: "Houston",              slugs: ["micasita.ca/blogs/houston"] },
      { label: "New York",             slugs: ["micasita.ca/blogs/new-york"] },
      { label: "New Orleans",          slugs: ["micasita.ca/blogs/new-orleans"] },
      { label: "Chicago",              slugs: ["micasita.ca/blogs/chicago"] },
      { label: "San Antonio",          slugs: ["micasita.ca/blogs/san-antonio"] },
      { label: "San Diego",            slugs: ["micasita.ca/blogs/san-diego"] },
      { label: "Dallas",               slugs: ["micasita.ca/blogs/dallas"] },
      { label: "El Paso",              slugs: ["micasita.ca/blogs/el-paso"] },
      { label: "Calgary",              slugs: ["micasita.ca/blogs/calgary"] },
      { label: "Montreal",             slugs: ["micasita.ca/blogs/montreal"] },
      { label: "Toronto",              slugs: ["micasita.ca/blogs/toronto"] },
      { label: "BC",                   slugs: ["micasita.ca/blogs/bc"] },
      { label: "Canada",               slugs: ["micasita.ca/blogs/canada"] },
      { label: "New Brunswick",        slugs: ["micasita.ca/blogs/new-brunswick"] },
      { label: "Real Estate News",     slugs: ["spanglishrealestate.substack.com/p"] },
    ],
  },
  {
    id: "legal", label: "Immigration & Law", icon: "⚖️", sub: "Immigration · Family · Business Law",
    subtopics: [
      { label: "US Immigration",     slugs: ["cozlaw.com/blogs/us-immigration-help"] },
      { label: "US Injury Law",      slugs: ["cozlaw.com/blogs/us-injury-law"] },
      { label: "US Family Law",      slugs: ["cozlaw.com/blogs/us-family-law"] },
      { label: "US Criminal Defense",slugs: ["cozlaw.com/blogs/us-criminal-defense"] },
      { label: "US Business Law",    slugs: ["cozlaw.com/blogs/us-business-law"] },
      { label: "Canada Immigration", slugs: ["cozlaw.com/blogs/canada-immigration-help"] },
      { label: "Canada Family Law",  slugs: ["cozlaw.com/blogs/canada-family-law"] },
      { label: "Canada Injury Law",  slugs: ["cozlaw.com/blogs/canada-injury-law"] },
      { label: "Canada Business Law",slugs: ["cozlaw.com/blogs/canada-business-law"] },
    ],
  },
  {
    id: "business", label: "Business & Entrepreneurship", icon: "📈", sub: "Startups · Founders · Growth",
    subtopics: [
      { label: "Founder Stories",      slugs: ["biz-index.net/blogs/founder-stories"] },
      { label: "SaaS Tools",           slugs: ["biz-index.net/blogs/saas-tools"] },
      { label: "Startup Scene",        slugs: ["biz-index.net/blogs/startup-scene"] },
      { label: "Startup & Franchise",  slugs: ["biz-index.net/blogs/startup-and-franchise"] },
      { label: "Startup News",         slugs: ["biz-index.net/blogs/startup-news"] },
      { label: "Small Business Growth",slugs: ["biz-index.net/blogs/small-business-growth"] },
      { label: "Entrepreneur Tips",    slugs: ["biz-index.net/blogs/entrepreneur-tips"] },
      { label: "Pitch Events",         slugs: ["biz-index.net/blogs/pitch-events"] },
      { label: "Legal for Small Biz",  slugs: ["biz-index.net/blogs/legal-for-small-biz"] },
      { label: "Business Insurance",   slugs: ["biz-index.net/blogs/business-insurance"] },
      { label: "Tax & Accounting",     slugs: ["biz-index.net/blogs/tax-and-accounting"] },
      { label: "Insider Trades",       slugs: ["spanglishinsidertrades.substack.com/p"] },
      { label: "Tech Startups",        slugs: ["spanglishtechstartup.substack.com/p"] },
      { label: "Business (Substack)",  slugs: ["spanglishbusiness.substack.com/p"] },
      { label: "Markets",              slugs: ["mercadonews.com/blogs/market"] },
      { label: "Economy",              slugs: ["mercadonews.com/blogs/economy"] },
      { label: "Entrepreneurship",     slugs: ["mercadonews.com/blogs/playbook"] },
      { label: "Money",                slugs: ["mercadonews.com/blogs/money"] },
    ],
  },
  {
    id: "health_wellness", label: "Health & Wellness", icon: "🧠", sub: "Mental Health · Research · Gut Health",
    subtopics: [
      { label: "Gut Health",       slugs: ["daymed.com/blogs/gut-health"] },
      { label: "Chronic Illness",  slugs: ["daymed.com/blogs/chronic-illness"] },
      { label: "Disease Outbreaks",slugs: ["daymed.com/blogs/disease-outbreaks"] },
      { label: "Sleep & Recovery", slugs: ["daymed.com/blogs/sleep-and-recovery"] },
      { label: "Health Insurance", slugs: ["daymed.com/blogs/health-insurance"] },
      { label: "Medical Research", slugs: ["daymed.com/blogs/medical-research"] },
      { label: "Mental Health",    slugs: ["daymed.com/blogs/mental-health"] },
    ],
  },
  {
    id: "lifestyle", label: "Food & Lifestyle", icon: "🌿", sub: "Recipes · Wellness · Culture · Soccer",
    subtopics: [
      { label: "Natural Remedies",  slugs: ["eat9.ca/blogs/natural-remedies"] },
      { label: "Clean Eating",      slugs: ["eat9.ca/blogs/clean-eating"] },
      { label: "Healthy Recipes",   slugs: ["eat9.ca/blogs/healthy-recipes"] },
      { label: "Detox & Gut Health",slugs: ["eat9.ca/blogs/detox-and-gut-health"] },
      { label: "Green Products",    slugs: ["eat9.ca/blogs/green-products"] },
      { label: "Organic Food",      slugs: ["eat9.ca/blogs/organic-food"] },
      { label: "Sustainable Living",slugs: ["eat9.ca/blogs/sustainable-living"] },
      { label: "Culture",           slugs: ["spanglishentertainment.substack.com/p"] },
      { label: "Soccer",            slugs: ["spanglishsoccer.substack.com/p"] },
      { label: "Living Abroad",     slugs: ["mercadonews.com/blogs/abroad"] },
    ],
  },
];

// ─── Progress Bar ──────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  const steps = [
    { n: 1, label: "Welcome" },
    { n: 2, label: "LinkedIn" },
    { n: 3, label: "Your voice" },
    { n: 4, label: "Your topics" },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 32 }}>
      {steps.map((s, i) => {
        const isDone   = step > s.n;
        const isActive = step === s.n;
        const dotColor = isDone ? COLORS.complete : isActive ? COLORS.active : COLORS.upcoming;
        const lineColor = isDone ? COLORS.complete : COLORS.upcoming;
        return (
          <React.Fragment key={s.n}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: dotColor,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 12, fontWeight: 700, transition: "background 0.2s",
              }}>
                {isDone ? "✓" : s.n}
              </div>
              <div style={{ fontSize: 10, color: isActive ? COLORS.active : "#999", fontWeight: isActive ? 600 : 400, whiteSpace: "nowrap" }}>{s.label}</div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: lineColor, margin: "0 4px", marginBottom: 16, transition: "background 0.2s" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Primary Button ────────────────────────────────────────────────────────────
function PrimaryBtn({ children, onClick, disabled, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        width: "100%", background: (disabled || loading) ? "#93c5db" : COLORS.blue,
        color: "#fff", border: "none", borderRadius: 10, padding: "15px",
        fontSize: 16, fontFamily: FONT, cursor: (disabled || loading) ? "not-allowed" : "pointer",
        fontWeight: 600, transition: "background 0.2s",
      }}
    >
      {loading ? "Please wait..." : children}
    </button>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function InviteOnboarding() {
  const getHashParams = () => {
    const hash = window.location.hash || "";
    const qIdx = hash.indexOf("?");
    if (qIdx === -1) return {};
    const qs = hash.slice(qIdx + 1);
    return Object.fromEntries(new URLSearchParams(qs).entries());
  };

  const params        = getHashParams();
  const inviteToken   = params.token || null;
  const onboardToken  = params.onboard_token || null;

  // State
  const [step, setStep]             = useState(onboardToken ? 3 : 1);
  const [invite, setInvite]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [linkedInProfile, setLinkedInProfile] = useState(null);

  // Step 3 — voice (same as NectarOnboard)
  const [voicePath, setVoicePath]   = useState(null); // "dropdown" | "groups" | "tone"
  const [mbtiType, setMbtiType]     = useState(null);
  const [tone, setTone]             = useState(null);
  const [quizStep, setQuizStep]     = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});

  // Step 4 — topics (same as NectarOnboard)
  const [topicSelection, setTopicSelection] = useState({}); // { catId: "all" | ["sub1","sub2"] }
  const [selectedTopicIds, setSelectedTopicIds] = useState([]); // ordered cat IDs
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [topicWarning, setTopicWarning] = useState(false);

  const [completing, setCompleting] = useState(false);
  const [done, setDone]             = useState(false);

  const voiceReady = !!tone || !!mbtiType;

  // ── Validate invite token on mount ────────────────────────────────────────
  useEffect(() => {
    if (!inviteToken) {
      setError("No invitation token found.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/app/api/auth/invite/validate?token=${encodeURIComponent(inviteToken)}`)
      .then(r => r.json())
      .then(d => {
        if (d.valid) {
          setInvite(d);
        } else if (d.reason === "expired") {
          setError("Your invitation has expired. Please ask your admin to send a new one.");
        } else {
          setError(d.reason || "This invitation link is invalid.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Could not validate your invitation. Please check your connection and try again.");
        setLoading(false);
      });
  }, [inviteToken]); // eslint-disable-line

  // ── If onboard_token present, fetch LinkedIn profile and jump to step 3 ──
  useEffect(() => {
    if (!onboardToken) return;
    fetch(`${API_BASE}/app/api/onboard/linkedin-session?onboard_token=${encodeURIComponent(onboardToken)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.connected && d.linkedin_name) {
          setLinkedInProfile({ name: d.linkedin_name, photo: d.linkedin_profile_image, email: d.linkedin_email });
        }
      })
      .catch(() => {});
    setStep(3);
  }, [onboardToken]); // eslint-disable-line

  // ── Topic helpers (same as NectarOnboard) ─────────────────────────────────
  // topicSelection is the source of truth for WHAT is selected
  // selectedTopicIds tracks ORDER only
  // totalSelections: whole category = 1, each individual subtopic = 1
  const totalSelections = Object.values(topicSelection).reduce((sum, val) => {
    if (val === "all") return sum + 1;
    if (Array.isArray(val)) return sum + val.length;
    return sum;
  }, 0);

  const toggleTopic = (catId) => {
    if (topicSelection[catId] !== undefined) {
      const next = { ...topicSelection };
      delete next[catId];
      setTopicSelection(next);
      setSelectedTopicIds(prev => prev.filter(id => id !== catId));
      setTopicWarning(false);
    } else {
      if (totalSelections >= 5) {
        setTopicWarning(true);
        return;
      }
      setTopicSelection(prev => ({ ...prev, [catId]: "all" }));
      setSelectedTopicIds(prev => {
        if (prev.includes(catId)) return prev;
        return [...prev, catId];
      });
      setTopicWarning(false);
    }
  };

  const toggleSubtopic = (catId, subLabel) => {
    const cur = topicSelection[catId];
    const isRemoving = Array.isArray(cur) && cur.includes(subLabel);

    // Cap check on every ADD
    if (!isRemoving && totalSelections >= 5) {
      setTopicWarning(true);
      return;
    }
    setTopicWarning(false);

    // Add to order list if new category
    if (cur === undefined) {
      setSelectedTopicIds(prev => prev.includes(catId) ? prev : [...prev, catId]);
    }

    // Update selection
    setTopicSelection(prev => {
      const next = { ...prev };
      const curVal = next[catId];
      if (!curVal || curVal === "all") {
        const cat = CATEGORIES.find(c => c.id === catId);
        if (curVal === "all") {
          const allLabels = cat.subtopics.map(s => s.label).filter(l => l !== subLabel);
          if (allLabels.length === 0) { delete next[catId]; } else { next[catId] = allLabels; }
        } else {
          next[catId] = [subLabel];
        }
      } else {
        const arr = Array.isArray(curVal) ? [...curVal] : [];
        const idx = arr.indexOf(subLabel);
        if (idx >= 0) {
          arr.splice(idx, 1);
          if (arr.length === 0) { delete next[catId]; } else { next[catId] = arr; }
        } else {
          arr.push(subLabel);
          next[catId] = arr;
        }
      }
      return next;
    });

    // Remove from order list if last subtopic unchecked
    if (isRemoving && Array.isArray(cur) && cur.length === 1) {
      setSelectedTopicIds(prev => prev.filter(id => id !== catId));
    }
  };

  // ── Build topic payload for API ───────────────────────────────────────────
  const buildTopicPayload = () => {
    // Send topic IDs (not labels) — backend validates against VALID_TOPIC_IDS
    const topicSel = {};
    for (const id of selectedTopicIds) {
      const val = topicSelection[id];
      if (val === "all") {
        topicSel[id] = "all";
      } else {
        topicSel[id] = Array.isArray(val) ? val : [];
      }
    }
    return { topics: selectedTopicIds, topicSelection: topicSel };
  };

  // ── Complete handler ───────────────────────────────────────────────────────
  const handleComplete = async () => {
    setCompleting(true);
    const { topics, topicSelection: topicSel } = buildTopicPayload();
    try {
      const r = await fetch(`${API_BASE}/app/api/onboard/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          onboard_token: onboardToken,
          invite_token: inviteToken,
          tone: tone || null,
          topics,
          topic_selection: topicSel,
          mbti_type: mbtiType || null,
        }),
      });
      const d = await r.json();
      if (r.ok && (d.status === "ok" || d.success)) {
        setDone(true);
        if (d.session_token) {
          localStorage.setItem("nectar_st", d.session_token);
        }
        setTimeout(() => {
          window.location.hash = "/dashboard";
          window.location.reload();
        }, 2500);
      } else {
        alert(d.error || "Something went wrong. Please try again.");
        setCompleting(false);
      }
    } catch {
      alert("Network error — please check your connection and try again.");
      setCompleting(false);
    }
  };

  // ── Card wrapper ───────────────────────────────────────────────────────────
  const card = (children) => (
    <div style={{
      background: COLORS.card, borderRadius: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      maxWidth: 560, width: "100%", padding: "40px 40px 36px",
      boxSizing: "border-box",
    }}>
      {children}
    </div>
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageShell>
        {card(
          <div style={{ textAlign: "center", padding: "20px 0", color: "#999", fontSize: 14, fontFamily: FONT }}>
            Validating your invitation...
          </div>
        )}
      </PageShell>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <PageShell>
        {card(
          <div style={{ textAlign: "center", fontFamily: FONT }}>
            <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>Invitation Error</div>
            <p style={{ fontSize: 15, color: "#666", lineHeight: 1.6, marginBottom: 24 }}>{error}</p>
            <a href="https://brandhives.com" style={{ color: COLORS.blue, fontSize: 14 }}>Go to BrandHives</a>
          </div>
        )}
      </PageShell>
    );
  }

  // ── Done ───────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <PageShell>
        {card(
          <div style={{ textAlign: "center", fontFamily: FONT }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: "#E1F5EE", display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 20px", fontSize: 26,
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>You're all set!</div>
            <p style={{ fontSize: 15, color: "#666", lineHeight: 1.6 }}>
              Your account has been created and linked to <strong>{invite?.workspace_name || "your team"}</strong>.<br />
              Taking you to your dashboard now...
            </p>
          </div>
        )}
      </PageShell>
    );
  }

  // ── Step 1: Welcome ────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <PageShell>
        {card(
          <div style={{ fontFamily: FONT }}>
            <ProgressBar step={1} />
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{
                display: "inline-block", background: "#E1F5EE", color: "#0F6E56",
                padding: "4px 14px", borderRadius: 20, fontSize: 11, fontWeight: 500, marginBottom: 16,
              }}>
                Team Invitation
              </div>
              <h1 style={{ fontSize: 28, fontWeight: 700, color: COLORS.text, margin: "0 0 8px" }}>
                Welcome to {invite?.workspace_name || "your team"}
              </h1>
              {invite?.inviter_name && (
                <p style={{ fontSize: 14, color: COLORS.muted, margin: 0 }}>
                  {invite.inviter_name} invited you as a <strong style={{ color: COLORS.text }}>{invite.role || "member"}</strong>
                </p>
              )}
            </div>

            <div style={{ background: "#f8f8f6", borderRadius: 12, padding: "20px 24px", marginBottom: 28 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                As a team member you'll get:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { icon: "✦", text: "AI-written LinkedIn posts in your voice" },
                  { icon: "✦", text: "Automatic weekly scheduling and publishing" },
                  { icon: "✦", text: "Your own topics and writing style" },
                  { icon: "✦", text: "Weekly team plug posts that grow together" },
                ].map(({ icon, text }) => (
                  <div key={text} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ color: COLORS.green, fontSize: 12, marginTop: 2, flexShrink: 0 }}>{icon}</span>
                    <span style={{ fontSize: 14, color: "#444", lineHeight: 1.5 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <PrimaryBtn onClick={() => setStep(2)}>Get Started</PrimaryBtn>
          </div>
        )}
      </PageShell>
    );
  }

  // ── Step 2: LinkedIn ───────────────────────────────────────────────────────
  if (step === 2) {
    const tempId = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const linkedInUrl = `${API_BASE}/app/api/auth/linkedin/connect?temp_id=${tempId}&invite_token=${encodeURIComponent(inviteToken || "")}`;
    return (
      <PageShell>
        {card(
          <div style={{ fontFamily: FONT }}>
            <ProgressBar step={2} />
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 12, background: "#EBF5FB",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill={COLORS.blue}>
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
                </svg>
              </div>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: COLORS.text, margin: "0 0 10px" }}>
                Connect your LinkedIn account
              </h2>
              <p style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.6, maxWidth: 400, margin: "0 auto" }}>
                BrandHives writes posts on your behalf — we need your LinkedIn connection to publish them for you.
              </p>
            </div>

            <div style={{ background: "#F0FAF4", borderRadius: 10, padding: "16px 20px", marginBottom: 24 }}>
              <div style={{ fontSize: 13, color: "#0F6E56", lineHeight: 1.6 }}>
                <strong>What we use this for:</strong> Posting to your LinkedIn profile on a schedule you control. We never read your messages or connections.
              </div>
            </div>

            <a
              href={linkedInUrl}
              style={{
                display: "block", width: "100%", background: COLORS.blue,
                color: "#fff", border: "none", borderRadius: 10, padding: "15px",
                fontSize: 16, fontFamily: FONT, cursor: "pointer", fontWeight: 600,
                textAlign: "center", textDecoration: "none", boxSizing: "border-box",
                transition: "background 0.2s",
              }}
            >
              Connect with LinkedIn
            </a>

            <button
              onClick={() => setStep(1)}
              style={{ width: "100%", background: "transparent", border: "none", color: "#999", fontSize: 13, fontFamily: FONT, cursor: "pointer", marginTop: 14, padding: "8px 0" }}
            >
              Back
            </button>
          </div>
        )}
      </PageShell>
    );
  }

  // ── Step 3: Voice ──────────────────────────────────────────────────────────
  if (step === 3) {
    const firstName = linkedInProfile?.name?.split(" ")[0] || "";
    return (
      <PageShell>
        {card(
          <div style={{ fontFamily: FONT }}>
            <ProgressBar step={3} />

            {linkedInProfile && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8f8f6", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
                {linkedInProfile.photo && (
                  <img src={linkedInProfile.photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
                )}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{linkedInProfile.name}</div>
                  <div style={{ fontSize: 12, color: "#1D9E75" }}>LinkedIn connected</div>
                </div>
              </div>
            )}

            <div style={{ fontSize: 12, color: "#999", marginBottom: 8, fontFamily: FONT }}>Step 1 of 3</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 6px" }}>
              Pick your voice{firstName ? `, ${firstName}` : ""} 👋
            </h2>
            <p style={{ fontSize: 14, color: COLORS.muted, margin: "0 0 24px", lineHeight: 1.6 }}>
              This shapes every post we write for you. You can change it anytime.
            </p>

            {/* Tone cards — always visible */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
              {TONES.map(t => (
                <div
                  key={t.id}
                  onClick={() => setTone(t.id)}
                  style={{ border: `1px solid ${tone === t.id ? GOLD : BORDER}`, borderRadius: 10, padding: "16px 18px", cursor: "pointer", background: tone === t.id ? GOLD_DARK : "transparent", transition: "all 0.2s" }}
                >
                  <div style={{ fontSize: 16, fontWeight: 600, color: tone === t.id ? GOLD_DIM : COLORS.text, marginBottom: 4 }}>{t.label}</div>
                  <div style={{ fontSize: 13, color: "#555555", lineHeight: 1.5, fontFamily: FONT }}>{t.fullDesc}</div>
                </div>
              ))}
            </div>

            {/* MBTI section */}
            <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text, marginBottom: 14 }}>Know your Myers-Briggs type?</div>

              {/* Pick your type — expandable */}
              <div style={{ marginBottom: 8 }}>
                <div
                  onClick={() => { setVoicePath(voicePath === "dropdown" ? null : "dropdown"); setQuizStep(0); setQuizAnswers({}); }}
                  style={{ border: `1.5px solid ${voicePath === "dropdown" ? GOLD : BORDER}`, borderRadius: 10, padding: "14px 18px", background: voicePath === "dropdown" ? GOLD_DARK : "transparent", display: "flex", gap: 14, alignItems: "center", cursor: "pointer", transition: "all 0.15s" }}
                >
                  <div style={{ fontSize: 22 }}>⚡</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: voicePath === "dropdown" ? GOLD_DIM : COLORS.text }}>Pick your type</div>
                    <div style={{ fontSize: 12, color: "#666666", marginTop: 2, fontFamily: FONT }}>Choose from the 16 types — for when you already know yours.</div>
                  </div>
                  <span style={{ fontSize: 13, color: "#BBBBBB", transform: voicePath === "dropdown" ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
                </div>
                {voicePath === "dropdown" && (
                  <div style={{ marginTop: 10, marginBottom: 8 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
                      {MBTI_TYPES.map(({ type, label }) => (
                        <button key={type} onClick={() => setMbtiType(type)}
                          style={{ padding: "10px 6px", borderRadius: 8, border: `1.5px solid ${mbtiType === type ? GOLD : BORDER}`, background: mbtiType === type ? GOLD_DARK : CARD, color: mbtiType === type ? GOLD_DIM : COLORS.text, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, lineHeight: 1.3, textAlign: "center" }}>
                          <div>{type}</div>
                          <div style={{ fontSize: 10, fontWeight: 400, color: mbtiType === type ? GOLD_DIM : "#888", marginTop: 2 }}>{label}</div>
                        </button>
                      ))}
                    </div>
                    {mbtiType && (
                      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 16px", background: GOLD_DARK }}>
                        <div style={{ fontSize: 13, color: GOLD_DIM, fontWeight: 700, fontFamily: FONT }}>{mbtiType} — {MBTI_TYPES.find(m => m.type === mbtiType)?.label}</div>
                        <div style={{ fontSize: 12, color: "#555555", marginTop: 4, fontFamily: FONT }}>{MBTI_DESCS[mbtiType]}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Take the quiz — expandable */}
              <div>
                <div
                  onClick={() => { setVoicePath(voicePath === "groups" ? null : "groups"); setQuizStep(0); setQuizAnswers({}); }}
                  style={{ border: `1.5px solid ${voicePath === "groups" ? GOLD : BORDER}`, borderRadius: 10, padding: "14px 18px", background: voicePath === "groups" ? GOLD_DARK : "transparent", display: "flex", gap: 14, alignItems: "center", cursor: "pointer", transition: "all 0.15s" }}
                >
                  <div style={{ fontSize: 22 }}>🧭</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: voicePath === "groups" ? GOLD_DIM : COLORS.text }}>Take the quiz</div>
                    <div style={{ fontSize: 12, color: "#666666", marginTop: 2, fontFamily: FONT }}>4 questions, 60 seconds — we'll find your type.</div>
                  </div>
                  <span style={{ fontSize: 13, color: "#BBBBBB", transform: voicePath === "groups" ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
                </div>
                {voicePath === "groups" && (() => {
                  const QUIZ = [
                    { key: "ei", question: "Where do you get your energy?",
                      options: [{ val: "E", emoji: "🤝", label: "From people", sub: "Energized by socializing and collaboration" },
                                 { val: "I", emoji: "🧘", label: "From alone time", sub: "Recharge by reflecting and working solo" }] },
                    { key: "sn", question: "How do you take in information?",
                      options: [{ val: "S", emoji: "🔍", label: "Facts & details", sub: "Trust what you can observe and measure" },
                                 { val: "N", emoji: "💡", label: "Patterns & ideas", sub: "Look for connections and big-picture meaning" }] },
                    { key: "tf", question: "How do you make decisions?",
                      options: [{ val: "T", emoji: "⚖️", label: "Logic & objectivity", sub: "Prioritize consistency and what makes sense" },
                                 { val: "F", emoji: "❤️", label: "Values & empathy", sub: "Prioritize people and what feels right" }] },
                    { key: "jp", question: "How do you approach life?",
                      options: [{ val: "J", emoji: "📋", label: "Planned & structured", sub: "Prefer clear goals and organized routines" },
                                 { val: "P", emoji: "🌊", label: "Flexible & spontaneous", sub: "Prefer to adapt and keep options open" }] },
                  ];
                  const answered = Object.keys(quizAnswers).length;
                  const allDone = answered === 4;
                  if (allDone && quizStep === 4) {
                    const result = (quizAnswers.ei||"E")+(quizAnswers.sn||"S")+(quizAnswers.tf||"T")+(quizAnswers.jp||"J");
                    const typeInfo = MBTI_TYPES.find(m => m.type === result);
                    return (
                      <div style={{ marginTop: 10, marginBottom: 8 }}>
                        <div style={{ border: `2px solid ${GOLD}`, borderRadius: 12, padding: "20px 18px", background: GOLD_DARK, textAlign: "center", marginBottom: 12 }}>
                          <div style={{ fontSize: 32, fontWeight: 700, color: GOLD_DIM, marginBottom: 4 }}>{result}</div>
                          <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>The {typeInfo?.label}</div>
                          <div style={{ fontSize: 13, color: "#555555", lineHeight: 1.6, fontFamily: FONT }}>{MBTI_DESCS[result]}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => { setQuizStep(0); setQuizAnswers({}); setMbtiType(null); }}
                            style={{ flex: 1, padding: "10px", background: "transparent", color: "#666", border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>
                            Retake quiz
                          </button>
                          <button onClick={() => setMbtiType(result)}
                            style={{ flex: 2, padding: "10px", background: GOLD, color: BG, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
                            {mbtiType === result ? "✓ Saved as " + result : "Use " + result}
                          </button>
                        </div>
                      </div>
                    );
                  }
                  const q = QUIZ[Math.min(quizStep, 3)];
                  return (
                    <div style={{ marginTop: 10, marginBottom: 8 }}>
                      <div style={{ display: "flex", gap: 6, marginBottom: 16, justifyContent: "center" }}>
                        {QUIZ.map((_, i) => (
                          <div key={i} style={{ width: 28, height: 4, borderRadius: 2, background: i < answered ? GOLD : i === quizStep ? GOLD_DIM : BORDER, transition: "background 0.2s" }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 12, color: "#999", marginBottom: 6, fontFamily: FONT }}>Question {quizStep + 1} of 4</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.text, marginBottom: 14, lineHeight: 1.4 }}>{q.question}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {q.options.map(opt => (
                          <div key={opt.val}
                            onClick={() => {
                              const next = { ...quizAnswers, [q.key]: opt.val };
                              setQuizAnswers(next);
                              const nextStep = quizStep + 1;
                              setQuizStep(nextStep === 4 ? 4 : nextStep);
                            }}
                            style={{ border: `1.5px solid ${quizAnswers[q.key] === opt.val ? GOLD : BORDER}`, borderRadius: 10, padding: "14px 16px", background: quizAnswers[q.key] === opt.val ? GOLD_DARK : "transparent", display: "flex", gap: 14, alignItems: "center", cursor: "pointer", transition: "all 0.15s" }}>
                            <div style={{ fontSize: 24 }}>{opt.emoji}</div>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: quizAnswers[q.key] === opt.val ? GOLD_DIM : COLORS.text }}>{opt.label}</div>
                              <div style={{ fontSize: 12, color: "#666", marginTop: 2, fontFamily: FONT }}>{opt.sub}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {quizStep > 0 && (
                        <button onClick={() => setQuizStep(s => Math.max(0, s - 1))}
                          style={{ marginTop: 12, background: "transparent", color: "#666", border: "none", fontSize: 13, cursor: "pointer", padding: "4px 0", fontFamily: FONT }}>
                          ← Back
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setStep(2)} style={{ background: "transparent", color: "#666666", border: `1px solid ${BORDER}`, borderRadius: 9, padding: "14px 22px", fontSize: 15, cursor: "pointer", minHeight: 44, fontFamily: FONT }}>← Back</button>
              <button
                onClick={() => { if (voiceReady) setStep(4); }}
                disabled={!voiceReady}
                style={{ flex: 1, background: voiceReady ? GOLD : GOLD_DARK, color: voiceReady ? BG : "#999999", border: "none", borderRadius: 9, padding: "15px", fontSize: 16, cursor: voiceReady ? "pointer" : "not-allowed", fontFamily: FONT, fontWeight: 600, transition: "all 0.2s", minHeight: 44 }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}
      </PageShell>
    );
  }

  // ── Step 4: Topics (same as NectarOnboard) ─────────────────────────────────
  if (step === 4) {
    return (
      <PageShell>
        {card(
          <div style={{ fontFamily: FONT }}>
            <ProgressBar step={4} />

            <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 6px" }}>
              What do you want <em>to talk about?</em>
            </h2>
            <p style={{ fontSize: 14, color: COLORS.muted, margin: "0 0 4px" }}>
              Choose 3–5 topics · select in priority order
            </p>
            {topicWarning && (
              <div style={{ fontSize: 12, color: "#C05050", marginBottom: 8, fontFamily: FONT }}>Maximum 5 topics — remove one before adding another.</div>
            )}
            {totalSelections > 0 && totalSelections < 3 && (
              <div style={{ fontSize: 12, color: "#999", marginBottom: 8, fontFamily: FONT }}>Select {3 - totalSelections} more topic{3 - totalSelections !== 1 ? "s" : ""} to continue.</div>
            )}

            <div style={{ background: "#F3F2EF", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "#999999", lineHeight: 1.6, fontFamily: FONT }}>
                ✦ Topics 1–3 are your primary feed &nbsp;·&nbsp; Topics 4–5 used as alternates when available
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {CATEGORIES.map(c => {
                const sel = topicSelection[c.id] !== undefined;
                const expanded = expandedTopic === c.id;
                const selVal = topicSelection[c.id];
                const selSubs = sel && selVal !== "all" ? selVal : [];
                return (
                  <div key={c.id} style={{ border: `1px solid ${sel ? GOLD : BORDER}`, borderRadius: 10, overflow: "hidden", background: sel ? GOLD_DARK : "transparent", transition: "all 0.18s" }}>
                    <div style={{ display: "flex", alignItems: "center", padding: "13px 16px", cursor: "pointer", gap: 10 }} onClick={() => setExpandedTopic(expanded ? null : c.id)}>
                      <input type="checkbox" checked={sel} onClick={e => e.stopPropagation()} onChange={() => toggleTopic(c.id)} style={{ accentColor: GOLD, cursor: "pointer", width: 16, height: 16, flexShrink: 0 }} />
                      <span style={{ fontSize: 20 }}>{c.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, lineHeight: 1.3 }}>{c.label}</div>
                        <div style={{ fontSize: 12, color: "#666666", fontFamily: FONT }}>{c.sub}</div>
                      </div>
                      <span style={{ fontSize: 13, color: "#BBBBBB", transform: expanded ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
                    </div>
                    {expanded && (
                      <div style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 16px", background: "#FAFAFA" }} onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize: 11, color: "#999999", marginBottom: 8, fontFamily: FONT }}>SELECT SUBTOPICS · leave all unchecked for the full topic feed</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
                          {c.subtopics.map(st => {
                            const checked = Array.isArray(selSubs) && selSubs.includes(st.label);
                            return (
                              <label key={st.label} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 14, fontFamily: FONT, color: checked ? GOLD_DIM : "#666666" }}>
                                <input type="checkbox" checked={checked} onChange={() => toggleSubtopic(c.id, st.label)} style={{ accentColor: GOLD, cursor: "pointer" }} />
                                {st.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: 12, color: "#999999", lineHeight: 1.6, marginTop: 14, padding: "0 2px", fontFamily: FONT }}>
              The order you pick shapes your content mix. You can reorder or swap topics any time in Settings → Topics.
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button onClick={() => setStep(3)} style={{ background: "transparent", color: "#666666", border: `1px solid ${BORDER}`, borderRadius: 9, padding: "14px 22px", fontSize: 15, cursor: "pointer", minHeight: 44, fontFamily: FONT }}>← Back</button>
              <button
                onClick={() => { if (totalSelections >= 3) handleComplete(); }}
                disabled={totalSelections < 3 || completing}
                style={{ flex: 1, background: totalSelections >= 3 ? GOLD : GOLD_DARK, color: totalSelections >= 3 ? BG : "#999999", border: "none", borderRadius: 9, padding: "14px", fontSize: 16, cursor: totalSelections >= 3 ? "pointer" : "not-allowed", fontFamily: FONT, fontWeight: 600, transition: "all 0.2s", minHeight: 44 }}
              >
                {completing ? "Setting up..." : "Complete Setup →"}
              </button>
            </div>
          </div>
        )}
      </PageShell>
    );
  }

  return null;
}

// ─── Minimal page shell (no login gate) ───────────────────────────────────────
function PageShell({ children }) {
  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: FONT }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} body{background:#F3F2EF;-webkit-font-smoothing:antialiased;} input[type=checkbox]{accent-color:#0077B5;} @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} .ob-card{animation:fadeUp 0.35s ease forwards;}`}</style>

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: `1px solid ${COLORS.border}`, padding: "14px 28px", display: "flex", alignItems: "center" }}>
        <BrandLogo size="sm" />
      </div>

      {/* Card area */}
      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 24px 60px" }}>
        <div className="ob-card" style={{ width: "100%", maxWidth: 560 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
